import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Award, MapPin, Calendar, BarChart3, Leaf, Shield, Building2, Share2, FileText, HardDrive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';
import QRCode from 'qrcode';
import { calculateFYAllocation, getFYLabel } from '@/utils/financialYearUtils';

// State NGA factors for display
const STATE_FACTORS = {
  'QLD': { factor: 0.67, name: 'Queensland' },
  'NSW': { factor: 0.64, name: 'New South Wales' },
  'ACT': { factor: 0.64, name: 'Australian Capital Territory' },
  'VIC': { factor: 0.82, name: 'Victoria' },
  'SA': { factor: 0.25, name: 'South Australia' },
  'WA': { factor: 0.51, name: 'Western Australia' },
  'TAS': { factor: 0.15, name: 'Tasmania' },
  'NT': { factor: 0.54, name: 'Northern Territory' }
};

const PublicProfilePage = () => {
  const { slug } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);

  const profileUrl = `${window.location.origin}/profile/${slug}`;

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        // Fetch public profile from Supabase
        const { data: org, error: orgError } = await supabase
          .from('organisations')
          .select('id, name, state, reporting_year, employee_count, public_profile_enabled, public_profile_slug, public_profile_fy_mode, public_profile_fy_year')
          .eq('public_profile_slug', slug)
          .single();

        if (orgError || !org) {
          setError('not_found');
          setLoading(false);
          return;
        }

        if (!org.public_profile_enabled) {
          setError('private');
          setLoading(false);
          return;
        }

        // Determine FY mode and target year
        const fyMode = org.public_profile_fy_mode || 'all_time';
        const targetFY = org.public_profile_fy_year || new Date().getFullYear();

        // Fetch emissions summary - include start_date and end_date for pro-rata
        const { data: emissions } = await supabase
          .from('emissions_data')
          .select('co2_equivalent, category, start_date, end_date, entry_date')
          .eq('organisation_id', org.id);

        // Calculate totals with optional FY filtering and pro-rata allocation
        let totalEmissions = 0;
        const scopes = { 'Scope 1': 0, 'Scope 2': 0 };
        
        const scope1Categories = ['fuel_petrol', 'fuel_diesel', 'fuel_lpg', 'natural_gas'];
        const scope2Categories = ['electricity', 'electricity_greenpower'];
        
        if (emissions) {
          emissions.forEach(e => {
            let emissionAmount = e.co2_equivalent || 0;
            
            // If specific FY mode, use pro-rata allocation
            if (fyMode === 'specific_fy' && emissionAmount > 0) {
              const startDate = e.start_date || e.entry_date;
              const endDate = e.end_date || e.start_date || e.entry_date;
              const allocations = calculateFYAllocation(startDate, endDate, emissionAmount);
              const fyAllocation = allocations.find(a => a.fyYear === targetFY);
              emissionAmount = fyAllocation ? fyAllocation.amount : 0;
            }
            
            if (emissionAmount > 0) {
              // Only include Scope 1 & 2 in totals
              if (scope1Categories.includes(e.category)) {
                totalEmissions += emissionAmount;
                scopes['Scope 1'] += emissionAmount;
              } else if (scope2Categories.includes(e.category)) {
                totalEmissions += emissionAmount;
                scopes['Scope 2'] += emissionAmount;
              }
              // Scope 3 categories are not tracked
            }
          });
        }

        // Fetch evidence list (filenames only, not URLs)
        const { data: documents } = await supabase
          .from('documents')
          .select('filename, document_type, file_size')
          .eq('organisation_id', org.id)
          .limit(10);

        setProfile({
          name: org.name,
          state: org.state,
          reporting_year: org.reporting_year,
          employee_count: org.employee_count,
          total_emissions: totalEmissions,
          scopes,
          evidence_list: documents || [],
          show_intensity: true,
          fy_mode: fyMode,
          fy_year: targetFY,
          fy_label: fyMode === 'specific_fy' ? getFYLabel(targetFY) : 'All Time'
        });
        
        // Generate QR code
        const qr = await QRCode.toDataURL(profileUrl, {
          width: 200,
          margin: 2,
          color: {
            dark: '#003B5C',
            light: '#FFFFFF'
          }
        });
        setQrCodeUrl(qr);
      } catch (err) {
        console.error('Error fetching profile:', err);
        setError('not_found');
      } finally {
        setLoading(false);
      }
    };
    
    fetchProfile();
  }, [slug, profileUrl]);

  const copyProfileUrl = () => {
    navigator.clipboard.writeText(profileUrl);
    toast.success('Public URL copied to clipboard');
  };

  const shareProfile = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${profile?.name} - Carbon Verification`,
          text: `View verified carbon emissions data for ${profile?.name}`,
          url: profileUrl
        });
      } catch (err) {
        copyProfileUrl();
      }
    } else {
      copyProfileUrl();
    }
  };

  const stateInfo = profile?.state ? STATE_FACTORS[profile.state] : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-[#003B5C] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error === 'private') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-12 max-w-md text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-slate-400" />
          </div>
          <h1 className="text-2xl font-bold text-[#003B5C] mb-4">Private Profile</h1>
          <p className="text-slate-600">
            This organisation has chosen to keep their carbon data private. 
            Contact them directly to request access to their sustainability information.
          </p>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-12 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Building2 className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-[#003B5C] mb-4">Profile Not Found</h1>
          <p className="text-slate-600">
            This verification profile doesn't exist or has been removed.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Social Metadata */}
      <Helmet>
        <title>{profile.name} - Verified Carbon Profile | Lucent Sky ESG</title>
        <meta name="description" content={`Verified carbon emissions data for ${profile.name}. ${profile.total_emissions?.toFixed(2) || 0} tonnes CO₂-e. Calculated using 2026 NGA factors.`} />
        
        {/* OpenGraph for LinkedIn/Facebook */}
        <meta property="og:title" content={`${profile.name} - Verified Carbon Profile`} />
        <meta property="og:description" content={`${profile.total_emissions?.toFixed(2) || 0} tonnes CO₂-e | Verified 2026 NGA | Lucent Sky ESG`} />
        <meta property="og:image" content={`${window.location.origin}/og-verification-badge.png`} />
        <meta property="og:url" content={profileUrl} />
        <meta property="og:type" content="website" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${profile.name} - Verified Carbon Profile`} />
        <meta name="twitter:description" content={`${profile.total_emissions?.toFixed(2) || 0} tonnes CO₂-e verified by Lucent Sky ESG`} />
        <meta name="twitter:image" content={`${window.location.origin}/og-verification-badge.png`} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 py-8 px-4" data-testid="public-profile-page">
        {/* Certificate Container */}
        <div className="max-w-3xl mx-auto">
          {/* Certificate Card */}
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border-4 border-[#003B5C]/10" data-testid="verification-certificate">
            {/* Header Ribbon */}
            <div className="bg-gradient-to-r from-[#003B5C] to-[#005580] px-6 sm:px-10 py-8 text-center">
              <div className="flex items-center justify-center mb-4">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                  <Leaf className="w-8 h-8 text-white" />
                </div>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Carbon Verification Certificate
              </h1>
              <p className="text-white/70 text-sm">Issued by Lucent Sky ESG</p>
            </div>

            {/* Verified Badge */}
            <div className="flex justify-center -mt-6">
              <div className="bg-[#74A57F] text-white px-8 py-3 rounded-full shadow-lg flex items-center" data-testid="verified-badge">
                <Award className="w-6 h-6 mr-3" />
                <span className="font-bold text-lg">
                  Verified 2026 — {profile.state ? `${stateInfo?.name || profile.state}` : 'Australian'} NGA Factors v1.0
                </span>
              </div>
            </div>

            {/* Main Content */}
            <div className="px-6 sm:px-10 py-8">
              {/* Organisation Name */}
              <div className="text-center mb-8">
                <h2 className="text-3xl sm:text-4xl font-bold text-[#003B5C] mb-3" data-testid="org-name">
                  {profile.name}
                </h2>
                <div className="flex flex-wrap items-center justify-center gap-4 text-slate-600">
                  {profile.state && (
                    <span className="flex items-center">
                      <MapPin className="w-4 h-4 mr-1" />
                      {stateInfo?.name || profile.state}
                    </span>
                  )}
                  {profile.reporting_year && (
                    <span className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      FY {profile.reporting_year}
                    </span>
                  )}
                </div>
              </div>

              {/* State NGA Factor Notice */}
              {stateInfo && (
                <div className="bg-[#003B5C]/5 rounded-xl p-4 mb-8 text-center" data-testid="state-factor-notice">
                  <p className="text-sm text-[#003B5C]">
                    <Shield className="w-4 h-4 inline mr-2" />
                    Calculated using <strong>{stateInfo.name} 2026 Grid Intensity</strong> ({stateInfo.factor} kg CO₂-e/kWh)
                  </p>
                </div>
              )}

              {/* Key Metrics */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                {/* Total Emissions */}
                <div className="bg-gradient-to-br from-[#003B5C] to-[#005580] rounded-2xl p-6 text-center text-white" data-testid="total-emissions-card">
                  <BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-80" />
                  <p className="text-white/70 text-sm uppercase tracking-wide mb-2">
                    Total Carbon Footprint
                    {profile.fy_label && (
                      <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs">
                        {profile.fy_label}
                      </span>
                    )}
                  </p>
                  <p className="text-4xl sm:text-5xl font-bold" data-testid="total-emissions">
                    {profile.total_emissions?.toFixed(2) || '0.00'}
                  </p>
                  <p className="text-white/70 mt-1">tonnes CO₂-e</p>
                </div>

                {/* Intensity Metric */}
                {profile.show_intensity !== false && profile.employee_count && profile.total_emissions && (
                  <div className="bg-gradient-to-br from-[#74A57F] to-[#5F8575] rounded-2xl p-6 text-center text-white" data-testid="intensity-card">
                    <Leaf className="w-10 h-10 mx-auto mb-3 opacity-80" />
                    <p className="text-white/70 text-sm uppercase tracking-wide mb-2">Carbon Intensity</p>
                    <p className="text-4xl sm:text-5xl font-bold">
                      {(profile.total_emissions / profile.employee_count).toFixed(2)}
                    </p>
                    <p className="text-white/70 mt-1">tCO₂-e per employee</p>
                  </div>
                )}
              </div>

              {/* Scope Breakdown */}
              {profile.scopes && Object.keys(profile.scopes).length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-[#003B5C] text-center mb-4">Emissions by Scope</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {['Scope 1', 'Scope 2'].map((scope) => (
                      <div key={scope} className="bg-slate-50 rounded-xl p-4 text-center">
                        <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">{scope}</p>
                        <p className="text-xl font-bold text-[#003B5C]">
                          {(profile.scopes[scope] || 0).toFixed(1)}
                        </p>
                        <p className="text-xs text-slate-500">tCO₂-e</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Evidence Index */}
              {profile.evidence_list && profile.evidence_list.length > 0 && (
                <div className="mb-8">
                  <div className="flex items-center justify-center space-x-2 mb-4">
                    <FileText className="w-5 h-5 text-[#74A57F]" />
                    <h3 className="text-lg font-semibold text-[#003B5C]">Evidence Index</h3>
                  </div>
                  <p className="text-xs text-slate-500 text-center mb-4">
                    Supporting documents uploaded to verify emission entries
                  </p>
                  <div className="bg-slate-50 rounded-xl p-4 max-h-48 overflow-y-auto">
                    <div className="space-y-2">
                      {profile.evidence_list.map((doc, index) => (
                        <div 
                          key={index}
                          className="flex items-center justify-between py-2 px-3 bg-white rounded-lg border border-slate-100"
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-[#003B5C]/10 rounded flex items-center justify-center">
                              <FileText className="w-4 h-4 text-[#003B5C]" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-700 truncate max-w-[200px]">
                                {doc.filename}
                              </p>
                              <p className="text-xs text-slate-400">
                                {doc.document_type || 'Document'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 text-xs text-slate-500">
                            <HardDrive className="w-3 h-3" />
                            <span>{doc.file_size}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-400 text-center mt-2 italic">
                    Files are stored securely. Only authorised users can access original documents.
                  </p>
                </div>
              )}

              {/* QR Code Section */}
              <div className="border-t-2 border-dashed border-slate-200 pt-8 flex flex-col sm:flex-row items-center justify-between gap-6">
                <div className="text-center sm:text-left">
                  <p className="text-sm text-slate-500 mb-2">Scan to verify</p>
                  {qrCodeUrl && (
                    <img 
                      src={qrCodeUrl} 
                      alt="Verification QR Code" 
                      className="w-32 h-32 mx-auto sm:mx-0 rounded-lg border border-slate-200"
                      data-testid="qr-code"
                    />
                  )}
                </div>
                <div className="text-center sm:text-right">
                  <p className="text-xs text-slate-400 mb-2">Verification ID</p>
                  <p className="font-mono text-sm text-slate-600 bg-slate-100 px-3 py-1 rounded">{slug}</p>
                  <Button
                    onClick={shareProfile}
                    variant="outline"
                    className="mt-4 border-[#003B5C] text-[#003B5C]"
                    data-testid="share-button"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share Profile
                  </Button>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-slate-50 px-6 sm:px-10 py-6 text-center border-t border-slate-100">
              <div className="flex items-center justify-center space-x-3 mb-3">
                <div className="w-8 h-8 bg-gradient-to-br from-[#003B5C] to-[#74A57F] rounded-lg flex items-center justify-center">
                  <Leaf className="w-4 h-4 text-white" />
                </div>
                <span className="font-bold text-[#003B5C]" style={{ fontFamily: 'Outfit, sans-serif' }}>Lucent Sky ESG</span>
              </div>
              <p className="text-xs text-slate-500 max-w-md mx-auto">
                This certificate verifies carbon emissions data reported through Lucent Sky ESG. 
                All calculations use 2026 National Greenhouse Accounts (NGA) factors as published 
                by the Australian Government.
              </p>
              <p className="text-xs text-slate-400 mt-3">
                lucentsky.com.au • Doonan, Australia
              </p>
            </div>
          </div>

          {/* Privacy Notice */}
          <div className="mt-6 text-center">
            <p className="text-xs text-slate-400">
              <Shield className="w-3 h-3 inline mr-1" />
              This public profile does not display individual invoices, financial data, or ABN details.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default PublicProfilePage;
